import { useCallback, useMemo, useRef, useState } from "react";

import { useBulkUpdateAssessment } from "@/hooks/mutations/use-assessments";
import { useAssessmentByCaseNumber } from "@/hooks/queries/use-assessments";
import { useCaseByCaseNumber } from "@/hooks/queries/use-cases";

import type {
  AssessmentFieldStatusSchema,
  BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch,
} from "@api/generated/types.gen";

import { ReactComponent as MicIcon } from "@assets/svg/mic-filled.svg";

import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";

import { cn } from "@utils/ui";

import {
  ASSESSMENT_STATUS,
  RESETTABLE_ASSESSMENT_FIELDS,
} from "../constants/sections";
import { RecordingSession } from "../hooks/useRecordingSession";
import { AudioRecorder } from "./AudioRecorder";
import { AudioUploader } from "./AudioUploader";
import type { SpeakerHint } from "./SpeakerChecklist";

type TranscriptField = AssessmentFieldStatusSchema | string | null | undefined;

const getFieldContent = (field: TranscriptField) => {
  if (!field) {
    return "";
  }

  if (typeof field === "string") {
    return field;
  }

  return field.content ?? "";
};

type TranscribeAndUploadButtonsProps = {
  caseNumber?: string;
  selectedId?: string;
  showButtons?: boolean;
  /**
   * Shared recording session (MDRS-116). Owned by the page so the consent modal
   * (start) and the in-panel ScribeSession (live view) drive the same session.
   */
  session: RecordingSession;
  /**
   * "header" (default) renders the compact top-bar buttons; "empty-state"
   * renders the centered no-content panel — mic pill, copy, and larger CTAs —
   * reusing the same start/upload flow (MDRS-113).
   */
  variant?: "header" | "empty-state";
};

export const TranscribeAndUploadButtons = ({
  caseNumber,
  selectedId,
  showButtons,
  session,
  variant = "header",
}: TranscribeAndUploadButtonsProps) => {
  const isEmptyState = variant === "empty-state";
  const { data: currentAssessment, isLoading: isAssessmentLoading } =
    useAssessmentByCaseNumber({ caseNumber, enabled: !!caseNumber });
  const { data: caseData, isLoading: isCaseLoading } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });

  const bulkUpdateAssessment = useBulkUpdateAssessment();

  const isLoading = isAssessmentLoading || isCaseLoading;

  const isTranscribing =
    currentAssessment?.status === ASSESSMENT_STATUS.TRANSCRIBING;

  // Determine if buttons should be shown: either explicitly set or when selectedId is "1"
  const shouldShowButtons = showButtons ?? selectedId === "1";

  // The consent modal is open from Start Session until the session actually
  // starts (or the user backs out). Once recording begins the LIVE view is in
  // the content panel (MDRS-116), so the modal closes.
  const [isConsentOpen, setIsConsentOpen] = useState(false);
  const [isOverwriteWarningOpen, setIsOverwriteWarningOpen] = useState(false);
  const confirmationResolverRef = useRef<((value: boolean) => void) | null>(null);

  const hasExistingFullTranscript = useMemo(() => {
    const content = getFieldContent(currentAssessment?.full_transcript).trim();

    if (!content) {
      return false;
    }

    return content.toLowerCase() !== "null";
  }, [currentAssessment?.full_transcript]);

  // A session occupies the content panel from the connecting handshake through
  // the stopped/complete state — block starting/uploading another until it's
  // dismissed (reset to idle).
  const sessionInProgress =
    session.isConnecting || session.isActive || session.isStopped;

  const resolvePendingConfirmation = useCallback((value: boolean) => {
    if (confirmationResolverRef.current) {
      confirmationResolverRef.current(value);
      confirmationResolverRef.current = null;
    }
  }, []);

  const requestOverwriteConfirmation = useCallback(() => {
    if (!hasExistingFullTranscript) {
      return Promise.resolve(true);
    }

    return new Promise<boolean>((resolve) => {
      confirmationResolverRef.current = resolve;
      setIsOverwriteWarningOpen(true);
    });
  }, [hasExistingFullTranscript]);

  const handleTranscribeClick = useCallback(async () => {
    if (isTranscribing || isLoading) {
      return;
    }

    const shouldProceed = await requestOverwriteConfirmation();
    if (!shouldProceed) {
      return;
    }

    setIsConsentOpen(true);
  }, [isLoading, isTranscribing, requestOverwriteConfirmation]);

  // Clear the existing transcript + summaries + notes the moment the user
  // confirms the overwrite (MDRS-132, 2026-07-08 stand-up). Best-effort: a failed
  // clear must not block the overwrite (the backend re-clears at summarize-start),
  // so handleOverwriteConfirm resolves regardless.
  const clearExistingAssessment = useCallback(async () => {
    const assessmentId = currentAssessment?.id;
    const caseId = currentAssessment?.case_id;
    if (!assessmentId || !caseId) {
      return;
    }

    const updates: Partial<
      Omit<BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch, "case_id">
    > = { segmented_full_transcript: "" };
    for (const field of RESETTABLE_ASSESSMENT_FIELDS) {
      updates[field] = "";
    }

    try {
      await bulkUpdateAssessment.mutateAsync({
        assessmentId,
        caseId,
        caseNumber,
        updates,
      });
    } catch (error) {
      // Non-blocking: the summarize-start backend clear is the fallback.
      console.error("Failed to clear assessment on overwrite:", error);
    }
  }, [
    bulkUpdateAssessment,
    caseNumber,
    currentAssessment?.case_id,
    currentAssessment?.id,
  ]);

  const handleOverwriteConfirm = useCallback(() => {
    setIsOverwriteWarningOpen(false);
    void clearExistingAssessment();
    resolvePendingConfirmation(true);
  }, [clearExistingAssessment, resolvePendingConfirmation]);

  const handleOverwriteCancel = useCallback(() => {
    setIsOverwriteWarningOpen(false);
    resolvePendingConfirmation(false);
  }, [resolvePendingConfirmation]);

  // Start the session from the consent gate. On success the live view takes
  // over the content panel and the modal closes; on failure the hook returns to
  // idle and we keep the modal open so the user can retry / back out.
  const handleStartRecording = useCallback(
    async (
      maxSpeakers: number,
      roleOrder: string[],
      speakerHints: SpeakerHint[]
    ) => {
      await session.start(maxSpeakers, roleOrder, speakerHints);
      setIsConsentOpen(false);
    },
    [session]
  );

  const handleConsentClose = useCallback(() => {
    setIsConsentOpen(false);
  }, []);

  // The header variant is gated by the active section; the empty-state variant
  // always renders (it owns the no-content panel).
  if (!isEmptyState && !shouldShowButtons) {
    return null;
  }

  const uploader = (
    <AudioUploader
      disabled={isTranscribing || isLoading || sessionInProgress}
      caseNumber={caseNumber || ""}
      examinee_name={caseData?.examinee_name || ""}
      onRequestOverwriteConfirm={requestOverwriteConfirmation}
      variant={variant}
    />
  );

  const startButton = (
    <Button
      size={isEmptyState ? "default" : "sm"}
      // Disable while a session is connecting/live/stopping so a second
      // session can't be started over the in-panel one. Show the current
      // state on the label + spinner so the button reflects what's happening.
      disabled={isTranscribing || isLoading || sessionInProgress}
      isLoading={session.isConnecting}
      onClick={handleTranscribeClick}
      className={cn(
        "flex items-center gap-1.5",
        isEmptyState && "gap-2 rounded-lg px-5 py-3 text-sm font-semibold"
      )}
    >
      {/* The SVG ships a hardcoded primary-blue fill; override it so the
          icon follows the button's foreground (white on the primary fill). */}
      {!session.isConnecting && (
        <MicIcon className="size-5 [&_path]:fill-primary-foreground" />
      )}
      {session.isConnecting
        ? "Connecting…"
        : sessionInProgress
          ? "Session in progress"
          : "Start Session"}
    </Button>
  );

  return (
    <>
      {isEmptyState ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-6 px-10 pb-[60px] pt-20 text-center">
          <div className="flex h-[52px] items-center justify-center rounded-full bg-primary-50 px-3.5">
            <MicIcon className="size-6 [&_path]:fill-primary" />
          </div>
          <div className="flex flex-col items-center gap-2.5">
            <h2 className="text-lg font-semibold text-foreground">
              No content available yet
            </h2>
            <p className="text-[13px] text-foreground-muted">
              Record your session live or upload an existing recording to generate
              AI-powered summaries for each section.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {uploader}
            {startButton}
          </div>
          <p className="text-[11px] text-foreground-muted">
            Supported formats: MP3, MP4, WAV, M4A · Max size 500MB
          </p>
        </div>
      ) : (
        <div className="flex gap-2">
          {uploader}
          {startButton}
        </div>
      )}

      {/* Consent gate — a modal (MDRS-115, kept in Phase 3). The LIVE session
          renders in the content panel once Start Recording is confirmed. */}
      <Dialog open={isConsentOpen} onOpenChange={setIsConsentOpen}>
        <DialogContent
          showCloseButton={false}
          className="max-w-lg"
          onInteractOutside={(event) => event.preventDefault()}
        >
          <AudioRecorder
            handleClose={handleConsentClose}
            examinee_name={caseData?.examinee_name || ""}
            caseNumber={caseNumber || ""}
            onStartRecording={handleStartRecording}
            isConnecting={session.isConnecting}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={isOverwriteWarningOpen}>
        <DialogContent
          showCloseButton={false}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Overwrite the full transcript?</DialogTitle>
            <DialogDescription>
              This case already has a Full Transcript. Starting a new transcription
              or uploading a new recording will overwrite the existing transcript for
              this case.
            </DialogDescription>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">
            If you want to add additional commentary, please update the individual
            modules or add it under &quot;Assessment notes&quot;.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={handleOverwriteCancel}>
              Cancel
            </Button>
            <Button onClick={handleOverwriteConfirm}>Overwrite transcript</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
